import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { sidebarSearchKeywordAtom } from '../sidebarSearchKeywordAtom';
import { includeViewsAtom, searchResultsAtom, type ViewModelJSON } from './trpcSearchResultState';
import { SearchViewItem } from './SearchViewItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { ViewModelItemTitle, RestrictedViewModelItemTitle } from '../../items';
import { classNames } from '@framework/utils';
import { useParams } from 'react-router';
import type { RouteParams } from '@user/pages/RouteParams';
import { usePrimitiveValueListener } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import { SearchViewModelTagItem } from './SearchViewModelTagItem';
import { useIsViewModelRestrictedByGroupLicensePermission } from '@group/License';
import { Timestamp } from '@framework/Timestamp';

type Props = {
  groupId: string;
  viewModel: ViewModelJSON;
  viewModelTags: { id: string; name: string }[];
  isIndividualPermissionEnabled: boolean;
};

export const SearchViewModelItem: React.FC<Props> = ({
  groupId,
  viewModel,
  viewModelTags,
  isIndividualPermissionEnabled,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const searchKeyword = useAtomValue(sidebarSearchKeywordAtom);
  const includeViews = useAtomValue(includeViewsAtom);
  const trpcResults = useAtomValue(searchResultsAtom);

  useEffect(() => {
    if (searchKeyword) setIsOpen(true);
  }, [searchKeyword]);

  const sortedViews = useMemo(() => {
    if (!includeViews) return [];

    if (!trpcResults) return [];

    const { views } = trpcResults.results;
    if (!views || !views[viewModel.id]) return [];

    return views[viewModel.id].sort((a, b) => a.name.localeCompare(b.name));
  }, [includeViews, trpcResults, viewModel.id]);

  const toggleViewModel = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const { viewModelId: viewModelIdParam } = useParams<RouteParams>();
  const isSelected = useMemo(() => viewModel.id === viewModelIdParam, [viewModel.id, viewModelIdParam]);

  const [individualPermissions] = usePrimitiveValueListener(RTDBPath.ViewModel.individualPermissionsPath(viewModel.id));

  const isRestricted = useIsViewModelRestrictedByGroupLicensePermission(new Timestamp(viewModel.createdAt));

  // 制限時のレンダリング
  if (isRestricted) {
    return (
      <div className="relative">
        <div className="flex items-center">
          {/* 展開ボタンはグレー表示、クリック無効 */}
          {includeViews && (
            <div className="mr-1 w-4">
              <FontAwesomeIcon icon={faCaretRight} className="size-4 text-gray-400" />
            </div>
          )}
          <div className="grow cursor-not-allowed">
            <RestrictedViewModelItemTitle viewModelName={viewModel.name.toString()} isLocked={viewModel.isLocked} />
          </div>
        </div>
        {/* 配下のビューは非表示 */}
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center">
        {includeViews && (
          <button className="mr-1 w-4 focus:outline-none" onClick={toggleViewModel}>
            <FontAwesomeIcon icon={isOpen ? faCaretDown : faCaretRight} className="size-4" />
          </button>
        )}
        <div className={classNames('grow', isSelected ? 'bg-gray-300 hover:bg-gray-400' : 'hover:bg-gray-300')}>
          <ViewModelItemTitle
            viewModelId={viewModel.id}
            isViewModelPublicReadable={viewModel.isPublicReadable}
            isIndividualPermissionConfigured={!!(isIndividualPermissionEnabled && individualPermissions)}
            isLocked={viewModel.isLocked}
            viewModelName={viewModel.name.toString()}
            searchKeyword={searchKeyword}
          />
          <div className="flex pl-5">
            {viewModelTags.map((tag) => (
              <SearchViewModelTagItem key={tag.id} groupId={groupId} tagId={tag.id} tagName={tag.name} />
            ))}
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="ml-5">
          {sortedViews.map((view) => (
            <SearchViewItem key={view.id} view={view} viewModel={viewModel} />
          ))}
        </div>
      )}
    </div>
  );
};
